import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { Website } from '@/api/types';
import { paths } from '@/app/paths';
import { Button, Segmented, useToast } from '@/design-system';
import { generateVariants } from '@/features/variants/api';
import { errorText } from '@/shell/errorText';
import { patchWebsite } from './api';
import type { StructureDoc } from './useStructure';
import s from './structure.module.css';

/** Saves the structure, starts N parallel variant jobs (P4-B04), opens Варианты and says how many started. */
export function useGenerate(site: Website, doc: StructureDoc) {
  const { t } = useTranslation('structure');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  /** `prompt`: the PM's words for this round (P4-B04 `GenerateIn.prompt`; «Из промпта библиотеки» sends its art direction). */
  const generate = async (prompt?: string | null) => {
    if (busy) return false;
    // «Из промпта библиотеки» has just pinned its lead section (saved by the flush below).
    if (!doc.secs.length && !prompt) {
      toast(t('bar.empty'));
      return false;
    }
    setBusy(true);
    try {
      await doc.flush();
      const out = await generateVariants(site.id, site.variant_count, prompt);
      void qc.invalidateQueries({ queryKey: ['websites', 'one', site.id] });
      void qc.invalidateQueries({ queryKey: ['variants', site.id] });
      navigate(paths.site(site.id, 'variants'));
      toast(t('bar.started', { count: out.variants.length || site.variant_count }));
      return true;
    } catch (e) {
      toast(errorText(e, tc));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { generate, busy };
}

/**
 * The sticky glass bar (every mode): «AI свяжет выбранные секции в один лендинг», the preferences
 * summary, «Вариантов» 1 / 2 / 3 (the website's `variant_count`) and «Сгенерировать».
 */
export function GenerateBar({ site, doc, summary, onGenerate }: { site: Website; doc: StructureDoc; summary: string; onGenerate?: () => void }) {
  const { t } = useTranslation('structure');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const { generate, busy } = useGenerate(site, doc);
  return (
    <div className={s.bar}>
      <div className={s.barText}>
        <span className={s.barTitle}>{t('bar.title')}</span>
        <span className={s.barSub}>{t('bar.by_prefs', { summary })}</span>
      </div>
      <div className={s.barCount}>
        <span className={s.barCountLabel}>{t('bar.variants')}</span>
        <Segmented
          variant="compact"
          className={s.countSeg}
          label={t('bar.variants_label')}
          value={site.variant_count}
          options={[1, 2, 3].map((n) => ({ value: n, label: String(n) }))}
          onChange={(n) => void patchWebsite(qc, site.id, { variant_count: n }).catch((e: unknown) => toast(errorText(e, tc)))}
        />
      </div>
      <Button icon="sparkles" onClick={() => (onGenerate ? onGenerate() : void generate())} aria-busy={busy}>
        {t('bar.generate')}
      </Button>
    </div>
  );
}
